"use client";

//import { memo } from "react";
import { useState, useEffect } from "react";
import type { PropsWithChildren } from "react";
import { Alert, Snackbar, Slide, Typography } from "@mui/material";
import type { SlideProps } from "@mui/material";

import { ToastContext, ToastAddMessage } from "./ToastContext";

import theme from "@/styles/scss/theme.module.scss";

const DEFAULT_DISMISS_TIMEOUT = 5000;

type ToastState = {
  open: boolean;
  timer?: ReturnType<typeof setTimeout>;
} & ToastAddMessage;

const initialState: ToastState = {
  open: false,
  title: "",
  type: "info",
};

function ToastProvider({ children }: PropsWithChildren) {
  const [toastState, setToastState] = useState<ToastState>(initialState);
  const [allMessages, setAllMessages] = useState<Array<ToastState>>([]);
  const [timeoutHolder, setTimeoutHolder] = useState<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * only show the oldest message
   */
  useEffect(() => {
    if (toastState.open || (allMessages ?? []).length <= 0) {
      return;
    }

    const clone: Array<ToastState> = JSON.parse(JSON.stringify(allMessages));
    const toSet: Array<ToastState> = clone.splice(0, 1);
    setToastState({ ...toSet[0], open: true });
    setAllMessages(clone);
  }, [allMessages, toastState]);

  /**
   * set a timeout when the toast state is set
   */
  useEffect(() => {
    if (!toastState.open) {
      return;
    }

    setTimeoutHolder(
      setTimeout(() => {
        const clone: ToastState = JSON.parse(JSON.stringify(toastState));
        clone.open = false;
        setToastState(clone);
      }, DEFAULT_DISMISS_TIMEOUT)
    );
  }, [toastState]);

  /**
   * expose a function to add a message for the toast to all messages
   */
  function addMessage(props: ToastAddMessage) {
    const clone: Array<ToastState> = JSON.parse(JSON.stringify(allMessages));
    clone.push({ open: false, ...props });
    setAllMessages(clone);
  }

  /**
   * transition effects for snackbar when it opens
   */
  function TransitionUp(props: Omit<SlideProps, "direction">) {
    return (
      <Slide
        {...props}
        direction="down"
      />
    );
  }

  /**
   * transition effects for snackbar when it opens
   */
  function dismiss() {
    const clone: ToastState = JSON.parse(JSON.stringify(toastState));
    clone.open = false;
    setToastState(clone);
    clearTimeout(timeoutHolder);
    setTimeoutHolder(undefined);
  }

  const variantStyles =
    toastState.style === "generic"
      ? {
          border: `1px solid ${theme.primaryMain}`,
          backgroundColor: theme.secondaryMain,
          color: theme.primaryMain,
          boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.20)",
          "& .MuiAlert-icon": {
            color: theme.primaryMain,
          },
        }
      : {};

  return (
    <ToastContext.Provider
      value={{
        addMessage,
      }}
    >
      {toastState.open && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={toastState.open}
          TransitionComponent={TransitionUp}
        >
          <Alert
            severity={toastState.type}
            sx={{
              width: "500px",
              maxWidth: "calc(100vw - 34px)",
              alignItems: "center",
              "& .MuiAlert-message": {
                width: "calc(100% - 32px)",
                overflow: "hidden",
              },
              overflow: "hidden",
              ...variantStyles,
            }}
            onClose={dismiss}
          >
            <div>
              <Typography variant="h6">{toastState.title}</Typography>
              <Typography variant="body2">
                {toastState.subtitle}
                {toastState.link && (
                  <>
                    {" "}
                    <a
                      href={toastState.link.href}
                      style={{
                        textDecoration: "underline",
                        color: theme.primaryMain,
                        fontWeight: "600",
                      }}
                      onClick={dismiss}
                    >
                      {toastState.link.text}
                    </a>
                  </>
                )}
              </Typography>
            </div>
          </Alert>
        </Snackbar>
      )}
      {children}
    </ToastContext.Provider>
  );
}

//const MemorizedProvider = memo(ToastProvider);
//export default MemorizedProvider;
export default ToastProvider;
